import type { Customer } from './types';

export type AgingLevel = 'old' | 'mid' | 'fresh';

/** Aging thresholds drive the pill style on the debt list: >30 / 15–30 / <15 ມື້. */
export function agingLevel(days: number): AgingLevel {
  if (days > 30) return 'old';
  if (days >= 15) return 'mid';
  return 'fresh';
}

export type DebtSort = 'oldest' | 'largest' | 'all';

export const DEBT_SORT_LABELS: Record<DebtSort, string> = {
  oldest: 'ຕິດດົນສຸດ',
  largest: 'ຫຼາຍສຸດ',
  all: 'ທັງໝົດ',
};

export function sortCustomers(list: Customer[], sort: DebtSort): Customer[] {
  const out = [...list];
  if (sort === 'oldest') out.sort((a, b) => b.days - a.days);
  if (sort === 'largest') out.sort((a, b) => b.balance - a.balance);
  return out;
}

export function totalOutstanding(list: Customer[]): number {
  return list.reduce((sum, c) => sum + c.balance, 0);
}

/**
 * Who the owner should chase today — the oldest debt that has actually come due.
 *
 * `reminderDays` is the shop's own "leave them alone until" threshold. Preferring
 * a debt that has reached it means the card stops nagging about a three-day-old
 * balance while a genuinely overdue one exists; when nothing is due yet it falls
 * back to the plain oldest, so the card is never empty while money is owed.
 */
export function chaseToday(list: Customer[], reminderDays = 0): Customer | undefined {
  const owing = list.filter((c) => c.balance > 0).sort((a, b) => b.days - a.days);
  return owing.find((c) => c.days >= reminderDays) ?? owing[0];
}

/** Fraction of the credit limit used, clamped to [0,1] for the progress bar. */
export function limitUsage(customer: Customer): number {
  if (customer.limit <= 0) return 0;
  return Math.min(1, customer.balance / customer.limit);
}

/**
 * How much of what the shop was owed it has actually brought in.
 *
 * Collected over collected-plus-outstanding, across the period the caller
 * passes. It answers the question the hero ring is really asking — "am I
 * getting my money back?" — rather than counting chases made.
 *
 * Returns `undefined` when there is nothing to measure: no debt and no
 * collections means the ring has no honest value, and 0% would read as "you
 * have collected none of it" to a shop that is simply owed nothing. The caller
 * shows nothing at all in that case.
 */
export function chaseProgress(collected: number, outstanding: number): number | undefined {
  const total = collected + outstanding;
  if (total <= 0) return undefined;
  return Math.max(0, Math.min(1, collected / total));
}
